import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { getCollectionName } from "../utils/environment";

// 컬렉션에서 모든 문서 가져오기
export const getAllDocuments = async (collectionName) => {
  try {
    const querySnapshot = await getDocs(collection(db, collectionName));
    const documents = [];
    querySnapshot.forEach((doc) => {
      documents.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    return documents;
  } catch (error) {
    console.error("Error getting documents:", error);
    throw error;
  }
};

// 특정 문서 가져오기
export const getDocument = async (collectionName, documentId) => {
  try {
    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting document:", error);
    throw error;
  }
};

// 조건부 쿼리로 문서 가져오기
export const getDocumentsByQuery = async (collectionName, conditions = []) => {
  try {
    let q = collection(db, collectionName);

    // 조건 추가
    conditions.forEach((condition) => {
      if (condition.type === "where") {
        q = query(
          q,
          where(condition.field, condition.operator, condition.value)
        );
      } else if (condition.type === "orderBy") {
        q = query(q, orderBy(condition.field, condition.direction || "asc"));
      } else if (condition.type === "limit") {
        q = query(q, limit(condition.value));
      }
    });

    const querySnapshot = await getDocs(q);
    const documents = [];
    querySnapshot.forEach((doc) => {
      documents.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    return documents;
  } catch (error) {
    console.error("Error getting documents by query:", error);
    throw error;
  }
};

// 실시간 리스너로 문서 가져오기
export const subscribeToCollection = (collectionName, callback) => {
  const unsubscribe = onSnapshot(
    collection(db, collectionName),
    (querySnapshot) => {
      const documents = [];
      querySnapshot.forEach((doc) => {
        documents.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      callback(documents);
    },
    (error) => {
      console.error("Error listening to collection:", error);
    }
  );

  return unsubscribe;
};

// 실시간 리스너로 특정 문서 가져오기
export const subscribeToDocument = (collectionName, documentId, callback) => {
  const docRef = doc(db, collectionName, documentId);
  const unsubscribe = onSnapshot(
    docRef,
    (docSnap) => {
      if (docSnap.exists()) {
        callback({
          id: docSnap.id,
          ...docSnap.data(),
        });
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error("Error listening to document:", error);
    }
  );

  return unsubscribe;
};

// 새 문서 추가
export const addDocument = async (collectionName, data) => {
  try {
    const docRef = await addDoc(collection(db, collectionName), data);
    return docRef.id;
  } catch (error) {
    console.error("Error adding document:", error);
    throw error;
  }
};

// 문서 업데이트
export const updateDocument = async (collectionName, documentId, data) => {
  try {
    const docRef = doc(db, collectionName, documentId);
    await updateDoc(docRef, data);
  } catch (error) {
    console.error("Error updating document:", error);
    throw error;
  }
};

// 문서 삭제
export const deleteDocument = async (collectionName, documentId) => {
  try {
    const docRef = doc(db, collectionName, documentId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting document:", error);
    throw error;
  }
};

// ==================== all_zoom_setting 전용 함수들 ====================

// all_zoom_setting 문서 가져오기
export const getZoomSettings = async () => {
  try {
    const collectionName = getCollectionName();
    const docRef = doc(db, collectionName, "all_zoom_setting");
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting zoom settings:", error);
    throw error;
  }
};

// all_zoom_setting 실시간 리스너
export const subscribeToZoomSettings = (callback) => {
  const collectionName = getCollectionName();
  const docRef = doc(db, collectionName, "all_zoom_setting");

  const unsubscribe = onSnapshot(
    docRef,
    (docSnap) => {
      if (docSnap.exists()) {
        callback({
          id: docSnap.id,
          ...docSnap.data(),
        });
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error("Error listening to zoom settings:", error);
    }
  );

  return unsubscribe;
};

// all_zoom_setting 업데이트
export const updateZoomSettings = async (data) => {
  try {
    const collectionName = getCollectionName();
    const docRef = doc(db, collectionName, "all_zoom_setting");
    await updateDoc(docRef, data);
  } catch (error) {
    console.error("Error updating zoom settings:", error);
    throw error;
  }
};

// all_zoom_setting 생성 (문서가 없을 경우)
export const createZoomSettings = async (data) => {
  try {
    const collectionName = getCollectionName();
    const docRef = doc(db, collectionName, "all_zoom_setting");
    await updateDoc(docRef, data);
  } catch (error) {
    console.error("Error creating zoom settings:", error);
    throw error;
  }
};

// all_zoom_notification 문서에서 list 필드 가져오기
export const getZoomNotifications = async () => {
  try {
    const collectionName = getCollectionName();
    const docRef = doc(db, collectionName, "all_zoom_notification");
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();

      const notifications = data.list || [];

      // created_at 기준으로 내림차순 정렬 (최신순)
      notifications.sort((a, b) => {
        const dateA =
          a.created_at instanceof Date ? a.created_at : new Date(a.created_at);
        const dateB =
          b.created_at instanceof Date ? b.created_at : new Date(b.created_at);
        return dateB - dateA;
      });

      return notifications;
    } else {
      return [];
    }
  } catch (error) {
    console.error("Error getting zoom notifications:", error);
    throw error;
  }
};

// all_zoom_notification 실시간 리스너
export const subscribeToZoomNotifications = (callback) => {
  const collectionName = getCollectionName();
  const docRef = doc(db, collectionName, "all_zoom_notification");

  const unsubscribe = onSnapshot(
    docRef,
    (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const notifications = data.list || [];

        // created_at 기준으로 내림차순 정렬
        notifications.sort((a, b) => {
          const dateA =
            a.created_at instanceof Date
              ? a.created_at
              : new Date(a.created_at);
          const dateB =
            b.created_at instanceof Date
              ? b.created_at
              : new Date(b.created_at);
          return dateB - dateA;
        });

        callback(notifications);
      } else {
        callback([]);
      }
    },
    (error) => {
      console.error("Error listening to zoom notifications:", error);
    }
  );

  return unsubscribe;
};
